import { useEffect, useMemo } from "react";
import { Check, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { localizeError } from "@/lib/errorMessages";

export interface ImageUploaderCardProps {
  file: File;
  /** {@code pending} = queued, {@code uploading} = in flight, {@code success | error} = terminal. */
  status: "pending" | "uploading" | "success" | "error";
  percent: number;
  error?: unknown | undefined;
  /** Hidden when the upload is in flight. */
  onRemove?: (() => void) | undefined;
  /** Shown only when {@code status === "error"}. */
  onRetry?: (() => void) | undefined;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ImageUploaderCard({
  file,
  status,
  percent,
  error,
  onRemove,
  onRetry,
}: ImageUploaderCardProps) {
  // Generate a thumbnail from the local File. Revoke when the file
  // identity changes or the card unmounts so we don't leak blob URLs.
  const previewUrl = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => {
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const errorMessage = status === "error" ? localizeError(error) : null;
  const showProgress = status === "uploading" || status === "pending";
  const showRemove = onRemove !== undefined && status !== "uploading";

  return (
    <div
      className={cn(
        "relative flex w-44 shrink-0 flex-col gap-2 rounded-lg border bg-card p-2 text-xs shadow-sm",
        status === "error" ? "border-destructive/40" : "border-border",
      )}
      data-testid={`image-card-${file.name}`}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-md bg-muted">
        <img
          src={previewUrl}
          alt={file.name}
          className="h-full w-full object-cover"
          loading="lazy"
        />
        {status === "success" ? (
          <div className="absolute inset-0 flex items-center justify-center bg-emerald-500/30">
            <Check
              aria-label="Đã tải lên"
              className="size-10 rounded-full bg-emerald-500 p-2 text-white"
            />
          </div>
        ) : null}
        {showRemove ? (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Bỏ ảnh ${file.name}`}
            className="absolute top-1 right-1 flex size-7 items-center justify-center rounded-full bg-background/80 text-foreground shadow-sm hover:bg-background"
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        ) : null}
      </div>

      <div className="min-w-0">
        <p className="truncate font-medium" title={file.name}>
          {file.name}
        </p>
        <p className="text-muted-foreground">{formatSize(file.size)}</p>
      </div>

      {showProgress ? (
        <div className="flex flex-col gap-1">
          <div
            role="progressbar"
            aria-label={`Tiến độ tải ${file.name}`}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
          >
            <div
              className="h-full bg-primary transition-[width] duration-150"
              style={{ width: `${percent}%` }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">{percent}%</p>
        </div>
      ) : null}

      {errorMessage !== null ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-[11px] text-destructive">{errorMessage}</p>
          {onRetry ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 w-full text-xs"
              onClick={onRetry}
            >
              <RotateCcw aria-hidden="true" className="size-3" />
              Thử lại
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
